import React, { useState } from 'react';
import { Icons } from '../components/Icons';
import MiniCipher from '../components/MiniCipher';
import { db, appId } from '../utils/firebase';
import { collection, addDoc, serverTimestamp, deleteDoc, doc } from 'firebase/firestore';

const CipherStudioView = ({ dbData, isAdmin, showToast }) => {
    const [faction, setFaction] = useState('TAG');
    const [activeTab, setActiveTab] = useState('studio');
    const [archiveFilter, setArchiveFilter] = useState('ALL');

    const [solomonInput, setSolomonInput] = useState('');
    const [criInput, setCriInput] = useState('');
    const [esoPath, setEsoPath] = useState([]);

    const allCiphers = dbData?.ciphers || [];
    const filteredCiphers = archiveFilter === 'ALL' ? allCiphers : allCiphers.filter(c => {
        if (archiveFilter === 'TAG') return c.type === 'solomon';
        if (archiveFilter === 'CRI') return c.type === 'cipher';
        if (archiveFilter === 'ESO') return c.type === 'sigil';
        return false;
    });

    const addDecorator = (char) => setSolomonInput(prev => prev + char);
    
    const handleUndo = () => {
        if (faction === 'TAG') {
            const lastChar = solomonInput.slice(-1);
            if (['☉','☽','♀','♂','✦','♄','☿','♃'].includes(lastChar)) {
                 setSolomonInput(prev => prev.slice(0, -1));
            } else {
                 setSolomonInput(prev => prev.slice(0, -1));
            }
        }
        if (faction === 'ESO') setEsoPath(prev => prev.slice(0, -1));
    };
    
    const clearCanvas = () => {
        setSolomonInput('');
        setCriInput('');
        setEsoPath([]);
    };

    const SIGIL_NODES = { 0:{cx:150,cy:150}, 1:{cx:150,cy:40}, 2:{cx:245,cy:95}, 3:{cx:245,cy:205}, 4:{cx:150,cy:260}, 5:{cx:55,cy:205}, 6:{cx:55,cy:95} };
    
    const handleEsoTap = (id) => {
        setEsoPath(prev => {
            const currentPath = [...prev];
            if (currentPath.length > 0 && currentPath[currentPath.length - 1].node === id) {
                const currentCurve = currentPath[currentPath.length - 1].curve || 0;
                currentPath[currentPath.length - 1].curve = (currentCurve + 1) % 3;
            } else if (currentPath.length < 31) { 
                currentPath.push({ node: id, curve: 0 }); 
            }
            return [...currentPath];
        });
    };

    const saveToDatabase = async () => {
        if (!isAdmin) return showToast("UNAUTHORIZED", "error");
        
        let payload = {
            code: `${faction}-`,
            type: '',
            visualData: null,
            timestamp: serverTimestamp()
        };

        if (faction === 'TAG') {
            if (!solomonInput) return showToast("Enter a word to generate a sigil.", "error");
            payload.type = 'solomon';
            payload.visualData = solomonInput;
            payload.code += solomonInput.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
        } else if (faction === 'CRI') {
            if (!criInput) return showToast("Enter text to generate a matrix.", "error");
            payload.type = 'cipher';
            payload.visualData = criInput;
            payload.code += criInput.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12);
        } else if (faction === 'ESO') {
            if (esoPath.length === 0) return showToast("Draw a sigil on the canvas first.", "error");
            payload.type = 'sigil';
            payload.visualData = JSON.stringify(esoPath);
            const wHex = esoPath.map(p => (p.node + (p.curve === 1 ? 8 : p.curve === 2 ? 16 : 0)).toString(36).toUpperCase()).join('');
            payload.code += wHex.match(/.{1,3}/g)?.join('-') || wHex;
        }

        try {
            await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'ciphers'), payload);
            showToast("Cipher saved to database.", "success");
            clearCanvas();
        } catch(e) { showToast("Firebase Error: Could not save cipher.", "error"); }
    };

    const handleDeleteCipher = async (e, id) => {
        e.stopPropagation();
        if (!window.confirm("Purge cipher from archive?")) return;
        try {
            await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'ciphers', id));
            showToast("Cipher deleted from database.", "success");
        } catch (err) { showToast("Error deleting cipher.", "error"); }
    };

    const handleLoadToStudio = (cipher) => {
        clearCanvas();
        if (cipher.type === 'solomon') {
            setFaction('TAG');
            setSolomonInput(cipher.visualData || '');
        } else if (cipher.type === 'cipher') {
            setFaction('CRI');
            setCriInput(cipher.visualData || '');
        } else if (cipher.type === 'sigil') {
            setFaction('ESO');
            try { setEsoPath(JSON.parse(cipher.visualData)); } catch(e) {}
        }
        setActiveTab('studio');
    };

    const factionStyles = {
        TAG: { color: 'text-red-500', bg: 'bg-red-900', border: 'border-red-500', name: 'TAG (Alphanumeric)' },
        CRI: { color: 'text-green-500', bg: 'bg-green-900', border: 'border-green-500', name: 'CRI (Matrix)' },
        ESO: { color: 'text-gray-300', bg: 'bg-gray-700', border: 'border-gray-500', name: 'ESO (Legacy)' },
    };
    const currentStyle = factionStyles[faction];
    const canUndo = (faction === 'TAG' && solomonInput.length > 0) || (faction === 'ESO' && esoPath.length > 0);

    const decoratorKeyboard = [
        { char: '☉', svg: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><circle cx="12" cy="12" r="5"></circle><line x1="12" y1="1" x2="12" y2="3"></line><line x1="12" y1="21" x2="12" y2="23"></line><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line><line x1="1" y1="12" x2="3" y2="12"></line><line x1="21" y1="12" x2="23" y2="12"></line><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line></svg> },
        { char: '☽', svg: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path></svg> },
        { char: '☿', svg: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><circle cx="12" cy="6" r="4"></circle><line x1="12" y1="10" x2="12" y2="22"></line><line x1="8" y1="16" x2="16" y2="16"></line><path d="M14 4a2 2 0 1 0-4 0"></path></svg> },
        { char: '♀', svg: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><circle cx="12" cy="9" r="6"></circle><line x1="12" y1="15" x2="12" y2="22"></line><line x1="8" y1="19" x2="16" y2="19"></line></svg> },
        { char: '♂', svg: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><circle cx="16" cy="8" r="5"></circle><line x1="6" y1="18" x2="12" y2="12"></line><line x1="12" y1="18" x2="12" y2="18"></line></svg> },
        { char: '♃', svg: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M2 12h5m15 0h-5"></path><path d="M7 12a5 5 0 0 1 5-5v10a5 5 0 0 1-10 0"></path></svg> },
        { char: '♄', svg: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M2 12h5m15 0h-5"></path><path d="M7 12a5 5 0 0 1 5-5v10"></path><line x1="12" y1="22" x2="12" y2="17"></line></svg> },
        { char: '✦', svg: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg> }
    ];

    return (
        <div className="flex-1 overflow-y-auto p-6 bg-[#050505] flex flex-col h-full custom-scrollbar relative fade-in">
            <div className="flex justify-between items-center mb-6 border-b border-gray-900 pb-4 shrink-0">
                <h2 className="text-xl font-bold flex items-center gap-2 text-white font-sans tracking-tight"><Icons.PenTool className={currentStyle.color} /> CIPHER STUDIO</h2>
                <div className="flex items-center gap-4">
                    <div className="flex bg-black rounded border border-gray-800 p-1">
                        <button onClick={() => setActiveTab('studio')} className={`px-4 py-1.5 text-xs font-bold uppercase rounded ${activeTab === 'studio' ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-white'}`}>STUDIO</button>
                        <button onClick={() => setActiveTab('archive')} className={`px-4 py-1.5 text-xs font-bold uppercase rounded ${activeTab === 'archive' ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-white'}`}>ARCHIVE ({allCiphers.length})</button>
                    </div>
                </div>
            </div>

            {activeTab === 'studio' ? (
                <div className="flex flex-col md:flex-row gap-8 shrink-0 mb-8">
                    <div className="flex-1 bg-[#0a0a0a] border border-gray-800 p-6 flex flex-col items-center justify-center relative min-h-[400px]">
                        <div className="absolute top-4 left-4 flex bg-black rounded border border-gray-800 p-1 z-10">
                            <button onClick={() => { setFaction('TAG'); clearCanvas(); }} className={`px-3 py-1 text-[10px] font-bold uppercase rounded ${faction === 'TAG' ? 'bg-red-900 text-white' : 'text-gray-500 hover:text-white'}`}>TAG</button>
                            <button onClick={() => { setFaction('CRI'); clearCanvas(); }} className={`px-3 py-1 text-[10px] font-bold uppercase rounded ${faction === 'CRI' ? 'bg-green-900 text-white' : 'text-gray-500 hover:text-white'}`}>CRI</button>
                            <button onClick={() => { setFaction('ESO'); clearCanvas(); }} className={`px-3 py-1 text-[10px] font-bold uppercase rounded ${faction === 'ESO' ? 'bg-gray-700 text-white' : 'text-gray-500 hover:text-white'}`}>ESO</button>
                        </div>
                        
                        <div className="w-full max-w-[360px] aspect-square relative mt-8 flex flex-col items-center justify-center">
                            {faction === 'TAG' && <MiniCipher type="solomon" data={solomonInput} size={360} svgId="export-svg" />}
                            {faction === 'CRI' && <MiniCipher type="cipher" data={criInput} size={360} svgId="export-svg" />}
                            {faction === 'ESO' && (
                                <svg id="export-svg" viewBox="0 0 300 300" className="w-full h-full absolute inset-0">
                                    <MiniCipher type="sigil" data={JSON.stringify(esoPath)} size={300} />
                                    {Object.entries(SIGIL_NODES).map(([id, pos]) => (
                                        <g key={id} onPointerDown={(e) => { e.stopPropagation(); handleEsoTap(parseInt(id)); }} className="cursor-pointer group ui-element">
                                            <circle cx={pos.cx} cy={pos.cy} r="25" fill="transparent" />
                                        </g>
                                    ))}
                                </svg>
                            )}
                        </div>
                    </div>

                    <div className="w-full md:w-80 flex flex-col gap-4 shrink-0">
                        <div className="bg-[#0a0a0a] border border-gray-800 p-6 flex flex-col justify-center text-center">
                            <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-4">{currentStyle.name} GENERATOR</h3>
                            {faction === 'TAG' && <input type="text" value={solomonInput} onChange={e=>setSolomonInput(e.target.value)} placeholder="TYPE TO DRAW..." className="w-full bg-[#111] border border-gray-700 p-3 text-white text-lg font-mono tracking-widest text-center focus:border-red-500 outline-none mb-4" />}
                            {faction === 'CRI' && <input type="text" value={criInput} onChange={e=>setCriInput(e.target.value)} placeholder="TYPE SENTENCE TO ENCODE..." className="w-full bg-[#111] border border-gray-700 p-3 text-white text-sm font-mono tracking-widest text-center focus:border-green-500 outline-none mb-4" />}
                            {faction === 'ESO' && <div className="text-xs text-gray-500 font-mono mb-4 h-[52px] flex items-center justify-center">DRAW ON THE CANVAS TO GENERATE A LEGACY SIGIL.</div>}

                            {isAdmin && (
                                <button onClick={saveToDatabase} className="w-full bg-blue-900/20 border border-blue-900/50 text-blue-500 px-4 py-3 text-xs font-bold uppercase tracking-widest hover:bg-blue-900 hover:text-white transition-colors flex items-center justify-center gap-2">
                                    <Icons.Save size={14} /> SAVE TO ARCHIVE
                                </button>
                            )}
                        </div>

                        {faction === 'TAG' && (
                             <div className="bg-[#0a0a0a] border border-red-900/30 p-4 flex flex-col gap-3 text-center">
                                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">DECORATOR KEYBOARD</span>
                                <div className="grid grid-cols-4 gap-2 text-red-500 justify-center">
                                    {decoratorKeyboard.map(({char, svg}) => (
                                        <button key={char} onClick={() => addDecorator(char)} className="p-2 bg-[#111] border border-gray-800 aspect-square flex items-center justify-center rounded hover:bg-red-500/20 hover:border-red-500/50 transition-colors">
                                            {svg}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        <div className="flex gap-2 mt-auto">
                            <button onClick={clearCanvas} className="flex-1 border border-red-900/50 text-red-500 bg-red-900/10 py-3 text-xs font-bold uppercase hover:bg-red-900 hover:text-white transition-colors" title="Clear Canvas">CLEAR</button>
                            {(faction === 'ESO' || faction === 'TAG') && 
                                <button onClick={handleUndo} disabled={!canUndo} className="w-1/4 border border-yellow-900/50 text-yellow-500 bg-yellow-900/10 py-3 text-xs font-bold uppercase hover:bg-yellow-900 hover:text-white transition-colors disabled:opacity-30" title="Undo"><Icons.Undo size={16} className="mx-auto" /></button>
                            }
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    <div className="mb-4 flex gap-1 bg-black p-1 rounded border border-gray-800 self-start">
                        <button onClick={() => setArchiveFilter('ALL')} className={`px-4 py-1.5 text-[10px] font-bold uppercase rounded ${archiveFilter === 'ALL' ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-white'}`}>ALL</button>
                        <button onClick={() => setArchiveFilter('TAG')} className={`px-4 py-1.5 text-[10px] font-bold uppercase rounded ${archiveFilter === 'TAG' ? 'bg-red-900 text-white' : 'text-gray-500 hover:text-white'}`}>TAG</button>
                        <button onClick={() => setArchiveFilter('CRI')} className={`px-4 py-1.5 text-[10px] font-bold uppercase rounded ${archiveFilter === 'CRI' ? 'bg-green-900 text-white' : 'text-gray-500 hover:text-white'}`}>CRI</button>
                        <button onClick={() => setArchiveFilter('ESO')} className={`px-4 py-1.5 text-[10px] font-bold uppercase rounded ${archiveFilter === 'ESO' ? 'bg-gray-700 text-white' : 'text-gray-500 hover:text-white'}`}>ESO</button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 pb-8">
                        {filteredCiphers.map(c => (
                            <div key={c.id} onClick={() => handleLoadToStudio(c)} className="bg-[#0a0a0a] border border-gray-800 p-4 rounded flex flex-col items-center text-center group hover:border-gray-500 transition-colors relative cursor-pointer shadow-lg min-h-[200px]">
                                {isAdmin && (
                                    <button onClick={(e) => handleDeleteCipher(e, c.id)} className="absolute top-2 right-2 text-gray-600 hover:text-red-500 z-10 p-1 bg-black/80 rounded" title="Delete Cipher">
                                        <Icons.Trash2 size={14} />
                                    </button>
                                )}
                                <MiniCipher type={c.type} data={c.visualData} size={120} />
                                <div className={`mt-4 text-[10px] font-bold tracking-widest uppercase truncate w-full px-2 ${c.type === 'solomon' ? 'text-red-500' : (c.type === 'cipher' ? 'text-green-500' : 'text-gray-300')}`}>{c.code}</div>
                            </div>
                        ))}
                        {filteredCiphers.length === 0 && <div className="col-span-full text-xs text-gray-600 font-mono text-center p-10 border border-dashed border-gray-800">NO {archiveFilter} CONNECTORS SAVED IN ARCHIVE.</div>}
                    </div>
                </>
            )}
        </div>
    );
};

export default CipherStudioView;